<script setup lang="ts">
import { computed, watchEffect } from "vue";

interface PhotoGallery {
  template: string;
  slug: string;
  title?: string;
  slideshow?: Array<{
    id: number | string;
    url: string;
    thumbUrl?: string;
    thumbWidth?: number;
    thumbHeight?: number;
    width?: number;
    height?: number;
    alt?: string;
    caption?: string;
  }>;
  coverImage?: { url: string; width?: number; height?: number; alt?: string };
}

const props = withDefaults(
  defineProps<{
    /** When provided (e.g. from a gallery block), filter galleries by these keywords (e.g. trek name). */
    filterKeywords?: string | string[];
    /** When true (e.g. gallery block with gallerystyle=Modal), open photo galleries in a modal instead of inline carousel. */
    openPhotosInModal?: boolean;
  }>(),
  { filterKeywords: undefined, openPhotosInModal: false },
);

const page = usePage().value;
interface Gallery {
  template: string;
  slug: string;
  title?: string;
}

interface Video {
  videoUrl: string;
  videoTitle: string;
}
const showModal = ref(false);
const selectedVideo = ref<{ videoUrl: string; videoTitle: string } | null>(
  null,
);

const showPhotoModal = ref(false);
const selectedPhotoGallery = ref<PhotoGallery | null>(null);

const closeModal = () => {
  showModal.value = false;
  selectedVideo.value = null;
};
const toggleModal = (video: Video) => {
  selectedVideo.value = video;
  showModal.value = !showModal.value;
};

const openPhotoGallery = (gallery: PhotoGallery) => {
  selectedPhotoGallery.value = gallery;
  showPhotoModal.value = true;
};
const closePhotoModal = () => {
  showPhotoModal.value = false;
  selectedPhotoGallery.value = null;
};

/** Normalized slideshow for LazyAppGallery (expects id: number and required thumb/width/height). */
const selectedPhotoSlideshow = computed(() => {
  const raw = selectedPhotoGallery.value?.slideshow ?? [];
  return raw.map((slide, index) => ({
    id: typeof slide.id === "number" ? slide.id : index,
    url: slide.url,
    thumbUrl: slide.thumbUrl ?? slide.url,
    thumbWidth: slide.thumbWidth ?? slide.width ?? 200,
    thumbHeight: slide.thumbHeight ?? slide.height ?? 200,
    width: slide.width ?? 0,
    height: slide.height ?? 0,
    alt: slide.alt ?? "",
    caption: slide.caption ?? "",
    class: "",
  }));
});

// Normalize for comparison: remove apostrophes, collapse spaces/dashes to single space, lowercase
const normalizeString = (str: string) => {
  return str
    .replace(/'/g, "")
    .replace(/[-_]+/g, " ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
};

/** When provided (e.g. from block), filter by these keywords instead of page.slug */
const keywordFilters = computed(() => {
  const raw = props.filterKeywords;
  if (raw == null || (Array.isArray(raw) && raw.length === 0)) return null;
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((k) => normalizeString(String(k))).filter(Boolean);
});

const matchesKeywords = (gallery: Gallery) => {
  if (!keywordFilters.value?.length) return false;
  const slugNorm = normalizeString(gallery.slug ?? "");
  const titleNorm = normalizeString(String(gallery.title ?? ""));
  return keywordFilters.value.some((kw) => {
    if (!kw) return false;
    return (
      slugNorm.includes(kw) ||
      titleNorm.includes(kw) ||
      kw.includes(slugNorm) ||
      kw.includes(titleNorm)
    );
  });
};

const filteredPhotoGalleries = computed(() => {
  const useKeywords = keywordFilters.value !== null;
  const normalizedPageTitle = normalizeString(page?.slug ?? "");

  return (
    page?.galleries?.filter((gallery: Gallery) => {
      if (gallery.template !== "album") return false;
      if (useKeywords) return matchesKeywords(gallery);
      const normalizedGalleryTitle = normalizeString(gallery.slug);
      return normalizedGalleryTitle.includes(normalizedPageTitle);
    }) || []
  );
});

const filteredVideoGalleries = computed(() => {
  const useKeywords = keywordFilters.value !== null;
  const normalizedPageTitle = normalizeString(page?.slug ?? "");

  return (
    page?.galleries?.filter((gallery: Gallery) => {
      if (gallery.template !== "video") return false;
      if (useKeywords) return matchesKeywords(gallery);
      const normalizedGalleryTitle = normalizeString(gallery.slug);
      return normalizedGalleryTitle.includes(normalizedPageTitle);
    }) || []
  );
});

const windowWidth = ref(0); // default value
const isMobile = computed(() => windowWidth.value <= 992);

const itemsToShow = ref(3.5);
const itemsToScroll = ref(1);

function updateWidth() {
  windowWidth.value = window.innerWidth;

  // Adjust itemsToShow and itemsToScroll based on isMobile value
  if (isMobile.value) {
    itemsToShow.value = 1.5;
    itemsToScroll.value = 1;
  } else {
    itemsToShow.value = 3.5;
    itemsToScroll.value = 1;
  }
}

const isPageLoaded = ref(false);

watchEffect(() => {
  const hasGalleries = !!page?.galleries;
  const hasFilter =
    !!page?.slug ||
    (props.filterKeywords != null &&
      (Array.isArray(props.filterKeywords)
        ? props.filterKeywords.length > 0
        : !!props.filterKeywords));
  if (hasGalleries && hasFilter) {
    isPageLoaded.value = true;
  }
});

onMounted(() => {
  updateWidth();
  window.addEventListener("resize", updateWidth);
  if (isPageLoaded.value) {
    // Page is loaded, perform any initialization needed
    // like adjusting carousel properties
    if (isMobile.value) {
      itemsToShow.value = 1.5;
      itemsToScroll.value = 1;
    } else {
      itemsToShow.value = 3.5;
      itemsToScroll.value = 1;
    }
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="pop" appear>
        <div
          v-if="showModal && selectedVideo"
          v-cloak
          class="modal video-modal"
        >
          <div class="video-wrapper">
            <iframe
              v-if="
                selectedVideo.videoUrl.includes('youtube.com') ||
                selectedVideo.videoUrl.includes('youtu.be')
              "
              width="560"
              height="315"
              :src="selectedVideo.videoUrl"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              allowfullscreen
            ></iframe>
            <iframe
              v-if="selectedVideo.videoUrl.includes('vimeo.com')"
              :src="selectedVideo.videoUrl"
              frameborder="0"
              :title="selectedVideo.videoTitle"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              allowfullscreen
            ></iframe>
            <AppSVGLoadingDots />
          </div>
        </div>
      </Transition>
      <Transition name="fade" appear>
        <div
          v-if="showModal && selectedVideo"
          v-cloak
          class="backdrop"
          @click="closeModal"
        ></div>
      </Transition>
      <!-- Photo gallery modal (when openPhotosInModal from gallery block) -->
      <Transition name="pop" appear>
        <div
          v-if="showPhotoModal && selectedPhotoGallery"
          v-cloak
          class="modal modal-gallery"
        >
          <div
            aria-label="Close gallery"
            class="close-modal"
            @click="closePhotoModal"
          />
          <LazyAppGallery
            v-if="selectedPhotoSlideshow.length"
            :slideshow="selectedPhotoSlideshow"
          />
        </div>
      </Transition>
      <Transition name="fade" appear>
        <div
          v-if="showPhotoModal"
          v-cloak
          class="backdrop"
          aria-hidden="true"
          @click="closePhotoModal"
        />
      </Transition>
    </Teleport>
  </ClientOnly>
  <section
    v-if="isPageLoaded && filteredPhotoGalleries.length"
    id="trekGalleries"
  >
    <h2>Galleries</h2>

    <ul class="gallery-wrap">
      <li
        v-for="(gallery, index) in filteredPhotoGalleries"
        :key="index"
        class="gallery"
      >
        <template v-if="openPhotosInModal">
          <button
            type="button"
            class="gallery-modal-trigger"
            @click="openPhotoGallery(gallery)"
          >
            <div class="gallery-image">
              <NuxtPicture
                v-if="gallery?.coverImage"
                format="webp"
                loading="lazy"
                fit="inside"
                :src="gallery.coverImage.url"
                :width="600"
                :height="300"
                :alt="gallery.coverImage.alt"
                sizes="sm:100vw md:50vw lg:600px"
              />
              <NuxtPicture
                v-else-if="gallery?.slideshow?.[0]"
                format="webp"
                loading="lazy"
                fit="inside"
                :src="gallery.slideshow[0].thumbUrl || gallery.slideshow[0].url"
                :width="600"
                :height="400"
                :alt="gallery.slideshow[0].alt"
                sizes="sm:100vw md:50vw lg:600px"
              />
            </div>
            <span class="block-gallery-modal-trigger-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4z" />
                <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
              </svg>
            </span>
            <h3>{{ gallery?.title }}</h3>
          </button>
        </template>
        <template v-else>
          <h3>{{ gallery?.title }}</h3>
          <Carousel
            :id="gallery.id"
            :items-to-show="itemsToShow"
            :items-to-scroll="itemsToScroll"
            :wrap-around="true"
          >
            <Slide
              v-for="slide in gallery.slideshow?.slice(0, 20)"
              :key="slide.id"
            >
              <div class="carousel__item">
                <NuxtPicture
                  format="webp"
                  loading="lazy"
                  :src="slide.thumbUrl"
                  :width="slide.thumbWidth"
                  :height="slide.thumbHeight"
                  :alt="slide.alt"
                />
              </div>
            </Slide>
            <template #addons>
              <Navigation />
            </template>
          </Carousel>
        </template>
      </li>
    </ul>

    <ul v-if="filteredVideoGalleries.length" class="gallery-wrap video-gallery">
      <li
        v-for="(gallery, index) in filteredVideoGalleries"
        :key="index"
        class="gallery video-carousel-wrap"
      >
        <h3>Videos</h3>
        <div v-if="gallery.videos">
          <Carousel
            v-if="gallery?.videos?.length > 4"
            :items-to-show="itemsToShow"
            :items-to-scroll="itemsToScroll"
            :wrap-around="false"
          >
            <Slide v-for="video in gallery?.videos" :key="video.id">
              <div class="carousel__item">
                <NuxtLink @click.prevent="toggleModal(video)">
                  <div class="play-button">
                    <svg viewBox="0 0 24 24">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="transparent"
                        stroke="#FFF"
                        stroke-width="1"
                      />
                      <polygon points="10,8 16,12 10,16" fill="#FFF" />
                    </svg>
                  </div>
                  <NuxtPicture
                    v-if="video?.videoThumb"
                    format="webp"
                    loading="lazy"
                    :src="video?.videoThumb?.url"
                    :width="video?.videoThumb?.width"
                    :height="video?.videoThumb?.height"
                    :alt="video?.videoThumb?.alt"
                    sizes="sm:100vw md:50vw lg:600px"
                  />
                  <!-- <h4 class="video-title">{{ video.videoTitle }}</h4> -->
                </NuxtLink>
              </div>
            </Slide>
            <template #addons>
              <Navigation />
            </template>
          </Carousel>
          <div v-else class="video-no-carousel">
            <div
              v-for="video in gallery?.videos"
              :key="video.id"
              class="carousel__item"
            >
              <NuxtLink @click.prevent="toggleModal(video)">
                <NuxtPicture
                  v-if="video?.videoThumb"
                  loading="lazy"
                  :src="video?.videoThumb?.url"
                  :width="video?.videoThumb?.width"
                  :height="video?.videoThumb?.height"
                  :alt="video?.videoThumb?.alt"
                  sizes="sm:100vw md:50vw lg:600px"
                />
                <!-- <h4 class="video-title">{{ video.videoTitle }}</h4> -->
              </NuxtLink>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>
